'use client';

import { observeRuntimeReleaseManifest } from '@/client/runtime-release-store';
import { createClientLogger, logClientWarning } from '@/client/logging/client-logger';
import { publicClientConfig } from '@/config/public-client-config';
import { createContext, ReactNode, useContext, useEffect, useRef, useState } from 'react';
import { ReviewEntity } from '@/entities/review.entity';
import { io } from 'socket.io-client';
import { SocketIoRawEvent } from '@/enums/socket-io-raw-event';
import { SocketIoEvents } from '@/events/socket-io.events';
import { CsvExportProgressEventEntity } from '@/entities/csv-export-progress-event.entity';
import { AutoReplyProgressEventEntity } from '@/entities/auto-reply-progress-event.entity';
import { AiSelectedAnalysisProgressEventEntity } from '@/entities/ai-selected-analysis-progress-event.entity';
import { ReviewAiJobProgressEventEntity } from '@/entities/review-ai-job-progress-event.entity';
import { ReviewAnswerJobSkippedEvent } from '@/entities/review-answer-job-skipped-event.entity';
import { ReviewFeedItemChangedEventEntity } from '@/entities/review-feed-item-changed-event.entity';
import { ReviewSelectionReconcileCompletedEventEntity } from '@/entities/review-selection-reconcile-completed-event.entity';
import { ClientReviewsSyncDoneEventEntity } from '@/entities/client-reviews-sync-done-event.entity';
import type { ReleaseManifest } from '@/shared/release-manifest';
import { buildSocketDiagnosticPayload } from '@/context/socket-io.diagnostics';

type SocketIoContextType = {
    events: SocketIoEvents;
    isConnected: boolean;
};

type ProviderProps = {
    authToken: string;
    children: ReactNode;
};

const events = new SocketIoEvents();
const socketLogger = createClientLogger({
    scope: 'socket',
});
const websocketUrl = publicClientConfig.websocketUrl;

const SocketIoContext = createContext<SocketIoContextType>({
    events,
    isConnected: false,
});

export const useSocketIo = () => {
    const context = useContext(SocketIoContext);
    return context;
};

export const SocketIoContextProvider = ({ authToken, children }: ProviderProps) => {
    const [isConnected, setIsConnected] = useState(false);
    const reconnectAttemptRef = useRef<number | null>(null);

    useEffect(() => {
        const socket = io(websocketUrl || undefined, {
            auth: { token: authToken },
            extraHeaders: { authorization: authToken },
            transports: ['websocket'],
        });
        const manager = socket.io;
        const getSocketDiagnosticSnapshot = () => {
            const managerState = manager as unknown as {
                _readyState?: string;
                engine?: {
                    transport?: {
                        name?: string;
                    };
                };
            };
            const configuredTransports = manager.opts.transports?.map((transport) =>
                typeof transport === 'string'
                    ? transport
                    : typeof transport?.name === 'string' && transport.name.trim()
                      ? transport.name
                      : 'anonymous_transport',
            );

            return {
                websocketUrl,
                path: manager.opts.path,
                configuredTransports,
                timeoutMs: manager.opts.timeout,
                socketConnected: socket.connected,
                socketId: socket.id,
                managerReadyState: managerState._readyState,
                activeTransport: managerState.engine?.transport?.name,
                reconnectAttempt: reconnectAttemptRef.current,
            };
        };
        socketLogger.debug('[client.realtime-socket.events] init', {
            url: websocketUrl || undefined,
            path: manager.opts.path,
            transports: manager.opts.transports,
            timeout: manager.opts.timeout,
        });

        const emitReviewAnswerDone = (data: { review: ReviewEntity; jobId: string }) => {
            socketLogger.debug('[client.realtime-socket.events] received raw reviewAnswerJobDone', {
                jobId: data.jobId,
                reviewId: data.review.id,
            });
            events.emit('reviewAnswerJobDone', data);
        };
        const emitReviewAnswerSkipped = (data: ReviewAnswerJobSkippedEvent) => {
            socketLogger.debug('[client.realtime-socket.events] received raw reviewAnswerJobSkipped', {
                jobId: data.jobId,
                jobName: data.jobName,
                reviewFeedItemId: data.reviewFeedItemId,
                reason: data.reason,
            });
            events.emit('reviewAnswerJobSkipped', data);
        };
        const emitAiSelectedAnalysisProgress = (data: AiSelectedAnalysisProgressEventEntity) => {
            socketLogger.debug('[client.realtime-socket.events] received raw aiSelectedAnalysisProgress', {
                operationId: data.operationId,
                stage: data.stage,
                processed: data.processed,
                total: data.total,
            });
            events.emit('aiSelectedAnalysisProgress', data);
        };
        const emitReviewAiJobProgress = (data: ReviewAiJobProgressEventEntity) => {
            socketLogger.debug('[client.realtime-socket.events] received raw reviewAiJobProgress', {
                operationId: data.operationId,
                jobName: data.jobName,
                stage: data.stage,
                reviewFeedItemId: data.reviewFeedItemId,
            });
            events.emit('reviewAiJobProgress', data);
        };
        const emitAutoReplyProgress = (data: AutoReplyProgressEventEntity) => {
            socketLogger.debug('[client.realtime-socket.events] received raw autoReplyProgress', {
                operationId: data.operationId,
                stage: data.stage,
                processed: data.processed,
                total: data.total,
            });
            events.emit('autoReplyProgress', data);
        };
        const emitReviewFeedItemChanged = (data: ReviewFeedItemChangedEventEntity) => {
            socketLogger.debug('[client.realtime-socket.events] received raw reviewFeedItemChanged', {
                reviewFeedItemId: data.reviewFeedItemId,
                reason: data.reason,
            });
            events.emit('reviewFeedItemChanged', data);
        };
        const emitClientSyncDone = (data: ClientReviewsSyncDoneEventEntity) => {
            socketLogger.debug('[client.realtime-socket.events] received raw clientReviewsSyncDone', data);
            events.emit('clientReviewsSyncDone', data);
        };
        const emitCsvExportProgress = (data: CsvExportProgressEventEntity) => {
            socketLogger.debug('[client.realtime-socket.events] received raw csvExportProgress', {
                operationId: data.exportOperationId,
                stage: data.stage,
                processedRows: data.processedRows,
                totalRows: data.totalRows,
            });
            events.emit('csvExportProgress', data);
        };
        const emitReviewSelectionReconcileCompleted = (data: ReviewSelectionReconcileCompletedEventEntity) => {
            socketLogger.debug('[client.realtime-socket.events] received raw reviewSelectionReconcileCompleted', {
                sessionId: data.sessionId,
                requestId: data.requestId,
                selectedCount: data.ids.length,
            });
            events.emit('reviewSelectionReconcileCompleted', data);
        };
        const observeRuntimeRelease = (data: ReleaseManifest) => {
            socketLogger.debug('[client.realtime-socket.events] received raw runtimeReleaseChanged', {
                revision: data.revision,
                protocolVersion: data.protocolVersion,
                minSupportedFrontendProtocolVersion: data.minSupportedFrontendProtocolVersion,
            });
            observeRuntimeReleaseManifest({
                backendManifest: data,
                source: 'socket',
            });
        };
        const onConnect = () => {
            reconnectAttemptRef.current = null;
            socketLogger.debug('[client.realtime-socket.events] connected', {
                socketId: socket.id,
                connected: socket.connected,
            });
            setIsConnected(true);
        };
        const onDisconnect = (reason: string) => {
            socketLogger.debug('[client.realtime-socket.events] disconnected', { reason });
            setIsConnected(false);
        };
        const onConnectError = (error: Error) => {
            const diagnostic = buildSocketDiagnosticPayload({
                event: 'connect_error',
                error,
                snapshot: getSocketDiagnosticSnapshot(),
            });
            logClientWarning({
                logger: socketLogger,
                message: diagnostic.message,
                error,
                context: diagnostic.context,
            });
        };
        const onSocketError = (error: Error) => {
            logClientWarning({
                logger: socketLogger,
                message: '[client.realtime-socket.socket-error] Socket error event received.',
                error,
            });
        };
        const onReconnectAttempt = (attempt: number) => {
            reconnectAttemptRef.current = attempt;
            socketLogger.debug('[client.realtime-socket.events] reconnect_attempt', { attempt });
        };
        const onReconnectError = (error: Error) => {
            const diagnostic = buildSocketDiagnosticPayload({
                event: 'reconnect_error',
                error,
                snapshot: getSocketDiagnosticSnapshot(),
            });
            logClientWarning({
                logger: socketLogger,
                message: diagnostic.message,
                error,
                context: diagnostic.context,
            });
        };
        const onReconnectFailed = () => {
            socketLogger.debug('[client.realtime-socket.events] reconnect_failed');
        };

        setIsConnected(socket.connected);
        socket.on(SocketIoRawEvent.ReviewAnswerJobDone, emitReviewAnswerDone);
        socket.on(SocketIoRawEvent.ReviewAnswerJobSkipped, emitReviewAnswerSkipped);
        socket.on(SocketIoRawEvent.AiSelectedAnalysisProgress, emitAiSelectedAnalysisProgress);
        socket.on(SocketIoRawEvent.ReviewAiJobProgress, emitReviewAiJobProgress);
        socket.on(SocketIoRawEvent.AutoReplyProgress, emitAutoReplyProgress);
        socket.on(SocketIoRawEvent.ReviewFeedItemChanged, emitReviewFeedItemChanged);
        socket.on(SocketIoRawEvent.ClientReviewsSyncDone, emitClientSyncDone);
        socket.on(SocketIoRawEvent.CsvExportProgress, emitCsvExportProgress);
        socket.on(SocketIoRawEvent.ReviewSelectionReconcileCompleted, emitReviewSelectionReconcileCompleted);
        socket.on(SocketIoRawEvent.RuntimeReleaseChanged, observeRuntimeRelease);
        socket.on('connect', onConnect);
        socket.on('disconnect', onDisconnect);
        socket.on('connect_error', onConnectError);
        socket.on('error', onSocketError);
        manager.on('reconnect_attempt', onReconnectAttempt);
        manager.on('reconnect_error', onReconnectError);
        manager.on('reconnect_failed', onReconnectFailed);

        return () => {
            socket.off(SocketIoRawEvent.ReviewAnswerJobDone, emitReviewAnswerDone);
            socket.off(SocketIoRawEvent.ReviewAnswerJobSkipped, emitReviewAnswerSkipped);
            socket.off(SocketIoRawEvent.AiSelectedAnalysisProgress, emitAiSelectedAnalysisProgress);
            socket.off(SocketIoRawEvent.ReviewAiJobProgress, emitReviewAiJobProgress);
            socket.off(SocketIoRawEvent.AutoReplyProgress, emitAutoReplyProgress);
            socket.off(SocketIoRawEvent.ReviewFeedItemChanged, emitReviewFeedItemChanged);
            socket.off(SocketIoRawEvent.ClientReviewsSyncDone, emitClientSyncDone);
            socket.off(SocketIoRawEvent.CsvExportProgress, emitCsvExportProgress);
            socket.off(SocketIoRawEvent.ReviewSelectionReconcileCompleted, emitReviewSelectionReconcileCompleted);
            socket.off(SocketIoRawEvent.RuntimeReleaseChanged, observeRuntimeRelease);
            socket.off('connect', onConnect);
            socket.off('disconnect', onDisconnect);
            socket.off('connect_error', onConnectError);
            socket.off('error', onSocketError);
            manager.off('reconnect_attempt', onReconnectAttempt);
            manager.off('reconnect_error', onReconnectError);
            manager.off('reconnect_failed', onReconnectFailed);
            socket.disconnect();
            setIsConnected(false);
            socketLogger.debug('[client.realtime-socket.events] disconnected in cleanup');
        };
    }, [authToken]);

    return <SocketIoContext.Provider value={{ events, isConnected }}>{children}</SocketIoContext.Provider>;
};
